/**
 * /assets — Fixed asset register + depreciation runner.
 *
 * Shows all assets with their cost, accumulated depreciation, net book
 * value, status, and method. The "Run Depreciation" button lets an
 * accountant pick a period and post the depreciation journal in one click.
 * Per-row actions place an asset in service (required before depreciation
 * can run against it) and dispose/sell it (posts the gain-or-loss journal).
 */
import { createFileRoute, useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, FormControl,
  FormControlLabel, IconButton, InputLabel, MenuItem, Paper, Select,
  Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, TextField, Tooltip, Typography, Alert,
} from "@mui/material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AddIcon from "@mui/icons-material/Add";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import { api, errorMessage } from "@/lib/api";
import { queryOptions } from "@tanstack/react-query";
import { accountsListQueryOptions, type AccountSummary } from "@/lib/queries";
import { NAV_HASH_CLICK_EVENT } from "@/lib/navLayouts";
import { ProjectSelect } from "@/components/ProjectSelect";
import type { ProjectSummary } from "@/lib/types";
import { HelpHint } from "@/components/HelpHint";
import { codeAndName } from "../lib/displayCode";

export const Route = createFileRoute("/_auth/assets")({
  component: AssetsPage,
});

// ── Types ──────────────────────────────────────────────────────────────────
interface FixedAsset {
  id: string;
  asset_no: string;
  description: string;
  asset_class: string | null;
  cost: string;
  salvage_value: string;
  useful_life_months: number;
  depreciation_method: string;
  // Lifetime unit estimate depreciation is spread across — only meaningful
  // for units_of_production (MONEY-FIXED-ASSETS-004). Optional for the same
  // reason as the §B fields below: an older row may not carry it.
  total_estimated_units?: string | null;
  status: string;
  acquired_date: string;
  in_service_date: string | null;
  disposed_date: string | null;
  periods_elapsed_at_start: number;
  opening_accum_depr: string;
  disposal_proceeds: string | null;
  disposal_journal_id: string | null;
  accumulated_depreciation: string;
  net_book_value: string;
  /** Count of OPEN fiscal periods since in-service with no posting yet. */
  depreciation_gap: number;
  // Always-editable metadata (PATCH /api/assets/{id} — MONEY-FIXED-ASSETS-003
  // §B). Optional: older rows / a not-yet-updated FixedAssetOut may omit
  // these, so the edit dialog falls back to blank rather than crashing.
  serial_no?: string | null;
  location?: string | null;
  notes?: string | null;
  project_id?: string | null;
  // Guarded (pristine-only) GL account FKs — same optionality reasoning.
  asset_account_id?: string | null;
  accum_depr_account_id?: string | null;
  depr_expense_account_id?: string | null;
}

interface FiscalPeriod {
  id: string;
  period_no: number;
  start_date: string;
  end_date: string;
  status: string;
}

interface AssetDisposalResult {
  asset_id: string;
  journal_id: string;
  proceeds: number;
  accumulated_depreciation: number;
  net_book_value: number;
  gain_loss: number;
}

// GAAP forward schedule row + tax-year row — see MONEY-TAX-DEPRECIATION-001 §5.
interface GaapScheduleRow {
  period_no: number;
  start_date: string;
  end_date: string;
  planned_amount: string | number;
  posted: boolean;
}

interface TaxScheduleRow {
  tax_year: number;
  convention: string;
  sec179: string | number;
  bonus: string | number;
  macrs: string | number;
  total: string | number;
}

interface SavedTaxProfile {
  macrs_class: string;
  sec179_amount: string | number;
  bonus_pct: string | number;
  tax_basis_override: string | number | null;
}

interface AssetScheduleResponse {
  gaap: GaapScheduleRow[];
  tax: TaxScheduleRow[] | null;
  profile: SavedTaxProfile | null;
}

interface TaxProfilePayload {
  macrs_class: string;
  sec179_amount: number;
  bonus_pct: number;
  tax_basis_override: number | null;
}

interface TieoutAccountRow {
  accum_depr_account_id: string;
  account_code: string;
  account_name: string;
  register_accumulated: number;
  gl_balance: number;
  delta: number;
  is_balanced: boolean;
}

interface AssetGlTieoutResponse {
  rows: TieoutAccountRow[];
  all_balanced: boolean;
}

const MACRS_CLASSES = ["3", "5", "7", "10", "15", "20", "27.5", "39"];

// ── Query options ──────────────────────────────────────────────────────────
const assetsQueryOptions = () =>
  queryOptions({
    queryKey: ["assets"],
    queryFn: () => api<FixedAsset[]>("/api/assets"),
  });

const periodsQueryOptions = () =>
  queryOptions({
    queryKey: ["fiscal-periods"],
    queryFn: () => api<FiscalPeriod[]>("/api/periods"),
  });

const assetScheduleQueryOptions = (assetId: string | undefined) =>
  queryOptions({
    queryKey: ["asset-schedule", assetId],
    queryFn: () => api<AssetScheduleResponse>(`/api/assets/${assetId}/schedule`),
    enabled: !!assetId,
  });

const assetGlTieoutQueryOptions = () =>
  queryOptions({
    queryKey: ["asset-gl-tieout"],
    queryFn: () => api<AssetGlTieoutResponse>("/api/reports/asset-gl-tieout"),
  });

function toCsv(header: string[], rows: (string | number)[][]): Blob {
  const esc = (v: string | number) => {
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [header.join(","), ...rows.map((r) => r.map(esc).join(","))];
  return new Blob([lines.join("\n")], { type: "text/csv" });
}

function downloadCsvBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ── Status chip ────────────────────────────────────────────────────────────
const STATUS_COLOR: Record<string, "default" | "success" | "warning" | "error"> = {
  acquired: "default",
  in_service: "success",
  idle: "warning",
  disposed: "error",
};

function fmtMoney(v: string | number) {
  const n = typeof v === "string" ? parseFloat(v) : v;
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

// ── Opening-pair cross-check (§F) ───────────────────────────────────────────
// Straight-line implied months from the opening $ balance. Null when
// cost/salvage/life don't support a stable per-period rate — never divide by
// zero on an incomplete or zero-life form.
function impliedMonthsFromOpening(
  cost: number, salvage: number, lifeMonths: number, opening: number,
): number | null {
  if (!(lifeMonths > 0)) return null;
  const perPeriod = (cost - salvage) / lifeMonths;
  if (!Number.isFinite(perPeriod) || perPeriod <= 0) return null;
  return Math.round(opening / perPeriod);
}

/**
 * Shared by the Add dialog's migrated-asset section and the Edit dialog's
 * guarded section (same cost/salvage/life/opening/months shape either way).
 * When opening > 0 and months is blank: in `autoApply` mode (Add dialog —
 * entry-time, nothing pre-exists yet), fills the implied months in with a
 * dismissible explanation; otherwise (Edit dialog) only SUGGESTS the implied
 * value with an explicit Apply button, since merely opening an edit dialog
 * must not mutate form state on its own (LOW-e). Either way, when both are
 * set and disagree by more than a month, warns inline without blocking
 * submission — the server tie-out report is the final arbiter, not this
 * heuristic.
 */
function OpeningPairCrossCheck({
  cost, salvage, lifeMonths, opening, months, onMonthsChange, autoApply = true,
}: {
  cost: string;
  salvage: string;
  lifeMonths: string;
  opening: string;
  months: string;
  onMonthsChange: (v: string) => void;
  autoApply?: boolean;
}) {
  const [dismissed, setDismissed] = useState(false);
  const openingN = parseFloat(opening) || 0;
  const monthsN = parseInt(months) || 0;
  const implied = impliedMonthsFromOpening(
    parseFloat(cost) || 0, parseFloat(salvage) || 0, parseInt(lifeMonths) || 0, openingN,
  );

  useEffect(() => {
    if (!autoApply) return;
    if (openingN > 0 && monthsN === 0 && implied !== null && implied > 0) {
      onMonthsChange(String(implied));
      setDismissed(false);
    }
    // Re-run only when the inputs that drive the implied value change — not
    // on every keystroke of `months`, or a user-cleared field would refill
    // itself before they can type a different number.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoApply, openingN, implied]);

  if (openingN <= 0 || implied === null) return null;

  if (!autoApply && monthsN === 0 && implied > 0) {
    return (
      <Alert
        severity="info"
        action={
          <Button color="inherit" size="small" onClick={() => onMonthsChange(String(implied))}>
            Apply
          </Button>
        }
      >
        {implied} month{implied === 1 ? "" : "s"} already depreciated is implied by the opening
        balance (straight-line). Apply to fill it in, or leave blank and enter your own.
      </Alert>
    );
  }

  if (!dismissed && monthsN === implied) {
    return (
      <Alert severity="info" onClose={() => setDismissed(true)}>
        Filled in {implied} month{implied === 1 ? "" : "s"} already depreciated — the straight-line
        value implied by the opening balance. Adjust if the prior system used a different method.
      </Alert>
    );
  }

  if (monthsN > 0 && Math.abs(monthsN - implied) > 1) {
    return (
      <Alert severity="warning">
        {monthsN} month{monthsN === 1 ? "" : "s"} already depreciated is more than a month off the
        straight-line implied value ({implied}) for this opening balance. Fine for declining-balance
        history or rounding — just confirm it's expected.
      </Alert>
    );
  }

  return null;
}

// ── Account picker ──────────────────────────────────────────────────────────
function AccountPicker({
  label, value, onChange, disabled, accountTypes, helperText,
}: {
  label: string;
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
  /** When set, only show accounts of these account_type values. */
  accountTypes?: string[];
  helperText?: string;
}) {
  const accountsQ = useQuery(accountsListQueryOptions());
  const options = (accountsQ.data ?? []).filter(
    (a) => !accountTypes || accountTypes.includes(a.account_type),
  );
  const selected = options.find((a) => a.id === value) ?? null;
  return (
    <Autocomplete
      size="small"
      disabled={disabled}
      options={options}
      getOptionLabel={(a: AccountSummary) => codeAndName(a)}
      value={selected}
      onChange={(_e, v) => onChange(v?.id ?? "")}
      loading={accountsQ.isLoading}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      renderInput={(params) => (
        <TextField {...params} label={label} helperText={helperText} />
      )}
    />
  );
}

// ── Main component ─────────────────────────────────────────────────────────
function AssetsPage() {
  const qc = useQueryClient();
  const assetsQ = useQuery(assetsQueryOptions());
  const periodsQ = useQuery(periodsQueryOptions());

  const [deprOpen, setDeprOpen] = useState(false);
  const [selectedPeriodId, setSelectedPeriodId] = useState("");
  const [deprResult, setDeprResult] = useState<{ total: number; count: number } | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [isMigrated, setIsMigrated] = useState(false);
  const emptyAsset = {
    asset_no: "", description: "", asset_class: "",
    acquired_date: "", in_service_date: "", cost: "", salvage_value: "0",
    useful_life_months: "60", depreciation_method: "straight_line",
    total_estimated_units: "",
    asset_account_id: "", accum_depr_account_id: "", depr_expense_account_id: "",
    periods_elapsed_at_start: "0", opening_accum_depr: "0",
    purchase_method: "cash",
    funding_account_id: "",
    equity_account_id: "",
  };
  const [newAsset, setNewAsset] = useState(emptyAsset);
  // units_of_production spreads cost over a lifetime unit count, so that
  // count is required. Caught here so the user sees it inline instead of
  // round-tripping to the server's 422.
  const addShowUnits = newAsset.depreciation_method === "units_of_production";
  const addUnitsInvalid = addShowUnits && !(parseFloat(newAsset.total_estimated_units) > 0);
  const addNeedsFunding =
    !isMigrated &&
    (newAsset.purchase_method === "cash" ||
      newAsset.purchase_method === "loan" ||
      newAsset.purchase_method === "lease");
  const addFundingInvalid = addNeedsFunding && !newAsset.funding_account_id;
  const addPurchaseMethodInvalid = !isMigrated && !newAsset.purchase_method;
  const addCostN = parseFloat(newAsset.cost) || 0;
  const addOpeningN = parseFloat(newAsset.opening_accum_depr) || 0;
  const addNbv = Math.max(0, addCostN - addOpeningN);
  const addCreateDisabled =
    addUnitsInvalid || addFundingInvalid || addPurchaseMethodInvalid;

  const [placeInServiceAsset, setPlaceInServiceAsset] = useState<FixedAsset | null>(null);
  const [placeInServiceDate, setPlaceInServiceDate] = useState(todayISO());

  // ── Edit dialog (§B) ───────────────────────────────────────────────────────
  const [editAsset, setEditAsset] = useState<FixedAsset | null>(null);
  const emptyEditForm = {
    asset_no: "", description: "", asset_class: "", serial_no: "", location: "", notes: "",
    cost: "", salvage_value: "", useful_life_months: "", depreciation_method: "straight_line",
    total_estimated_units: "",
    acquired_date: "", in_service_date: "",
    opening_accum_depr: "", periods_elapsed_at_start: "",
    asset_account_id: "", accum_depr_account_id: "", depr_expense_account_id: "",
  };
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [editProject, setEditProject] = useState<ProjectSummary | null>(null);
  // Whether the project-picker seed fetch has resolved. "pending" while the
  // request is in flight, "none-needed" when the asset has no project_id to
  // seed at all. project_id is only ever included in the PATCH once this is
  // "loaded" or "none-needed" — never while "pending" (the row's true value
  // hasn't been confirmed in the picker yet) and never on "failed" (M-4: a
  // slow or failed fetch must not read as "user cleared the project").
  type ProjectSeedState = "pending" | "loaded" | "failed" | "none-needed";
  const [projectSeedState, setProjectSeedState] = useState<ProjectSeedState>("none-needed");
  // Pristine determined client-side from the same numbers the row already
  // shows — no posted depreciation beyond the migrated opening balance means
  // the setup fields are still safe to touch. The backend re-checks (zero
  // DepreciationPosting rows AND zero revisions AND not disposed) and 409s
  // with the authoritative answer if this heuristic is ever wrong; this only
  // controls whether the UI offers the fields up front.
  const editIsDisposed = editAsset?.status === "disposed";
  const editHasPostedDepreciation =
    !!editAsset && parseFloat(editAsset.accumulated_depreciation) > parseFloat(editAsset.opening_accum_depr);
  const editGuardedDisabled = editIsDisposed || editHasPostedDepreciation;
  // Shown (read-only when guarded) whenever the method needs it; only blocks
  // the save when the field is actually editable — a guarded asset doesn't
  // send the setup block at all, so there is nothing to validate.
  const editShowUnits = editForm.depreciation_method === "units_of_production";
  const editUnitsInvalid =
    editShowUnits && !editGuardedDisabled && !(parseFloat(editForm.total_estimated_units) > 0);

  function openEditDialog(a: FixedAsset) {
    setEditAsset(a);
    setEditForm({
      asset_no: a.asset_no,
      description: a.description,
      asset_class: a.asset_class ?? "",
      serial_no: a.serial_no ?? "",
      location: a.location ?? "",
      notes: a.notes ?? "",
      cost: a.cost,
      salvage_value: a.salvage_value,
      useful_life_months: String(a.useful_life_months),
      depreciation_method: a.depreciation_method,
      total_estimated_units: a.total_estimated_units ?? "",
      acquired_date: a.acquired_date,
      in_service_date: a.in_service_date ?? "",
      opening_accum_depr: a.opening_accum_depr,
      periods_elapsed_at_start: String(a.periods_elapsed_at_start),
      asset_account_id: a.asset_account_id ?? "",
      accum_depr_account_id: a.accum_depr_account_id ?? "",
      depr_expense_account_id: a.depr_expense_account_id ?? "",
    });
    setEditProject(null);
    setProjectSeedState(a.project_id ? "pending" : "none-needed");
  }

  // Seed the project picker from the id the row carries — the register list
  // only has the id, not the code/name ProjectSelect needs to display it, so
  // this fetches the one project the dialog actually needs instead of
  // pulling the whole picker list just to find a match.
  useEffect(() => {
    if (!editAsset?.project_id) return;
    let cancelled = false;
    api<ProjectSummary>(`/api/projects/${editAsset.project_id}`)
      .then((p) => { if (!cancelled) { setEditProject(p); setProjectSeedState("loaded"); } })
      .catch(() => { if (!cancelled) setProjectSeedState("failed"); });
    return () => { cancelled = true; };
  }, [editAsset?.project_id]);

  const [disposeOpen, setDisposeOpen] = useState(false);
  const [disposeAsset, setDisposeAsset] = useState<FixedAsset | null>(null);
  const [disposalDate, setDisposalDate] = useState(todayISO());
  const [disposalPeriodId, setDisposalPeriodId] = useState("");
  const [proceeds, setProceeds] = useState("0");
  const [proceedsAccountId, setProceedsAccountId] = useState("");
  const [disposalResult, setDisposalResult] = useState<AssetDisposalResult | null>(null);

  const deprMut = useMutation({
    mutationFn: (periodId: string) =>
      api<{ amount: number; asset_id: string }[]>("/api/assets/depreciation/run", {
        method: "POST",
        body: JSON.stringify({ period_id: periodId }),
      }),
    onSuccess: (data) => {
      const total = data.reduce((s, r) => s + Number(r.amount), 0);
      setDeprResult({ total, count: data.length });
      void qc.invalidateQueries({ queryKey: ["assets"] });
    },
  });

  const addMut = useMutation({
    mutationFn: (payload: typeof newAsset & { isMigrated: boolean }) => {
      const purchase_method = payload.isMigrated ? "carried_over" : payload.purchase_method;
      const body: Record<string, unknown> = {
        asset_no: payload.asset_no,
        description: payload.description,
        asset_class: payload.asset_class || null,
        acquired_date: payload.acquired_date,
        in_service_date: payload.in_service_date || null,
        cost: parseFloat(payload.cost),
        salvage_value: parseFloat(payload.salvage_value),
        useful_life_months: parseInt(payload.useful_life_months),
        depreciation_method: payload.depreciation_method,
        // Only units_of_production stores a unit estimate — any other
        // method sends null rather than a leftover from a changed mind.
        total_estimated_units:
          payload.depreciation_method === "units_of_production"
            ? parseFloat(payload.total_estimated_units)
            : null,
        asset_account_id: payload.asset_account_id,
        accum_depr_account_id: payload.accum_depr_account_id,
        depr_expense_account_id: payload.depr_expense_account_id,
        periods_elapsed_at_start: parseInt(payload.periods_elapsed_at_start) || 0,
        opening_accum_depr: parseFloat(payload.opening_accum_depr) || 0,
        purchase_method,
      };
      if (purchase_method === "cash" || purchase_method === "loan" || purchase_method === "lease") {
        body.funding_account_id = payload.funding_account_id || null;
      }
      if (purchase_method === "carried_over" && payload.equity_account_id) {
        body.equity_account_id = payload.equity_account_id;
      }
      return api<FixedAsset>("/api/assets", {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["assets"] });
      setAddOpen(false);
      setIsMigrated(false);
      setNewAsset(emptyAsset);
    },
  });

  const placeInServiceMut = useMutation({
    mutationFn: ({ id, inServiceDate }: { id: string; inServiceDate: string }) =>
      api<FixedAsset>(`/api/assets/${id}/place-in-service`, {
        method: "POST",
        body: JSON.stringify({ in_service_date: inServiceDate || null }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["assets"] });
      setPlaceInServiceAsset(null);
    },
  });

  const editMut = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        asset_no: editForm.asset_no,
        description: editForm.description,
        asset_class: editForm.asset_class || null,
        serial_no: editForm.serial_no || null,
        location: editForm.location || null,
        notes: editForm.notes || null,
      };
      // M-4: only send project_id once the seed fetch has resolved to a
      // known value, and only when the picker's current value actually
      // differs from what the asset has on record — omitting the key (not
      // sending null) is what keeps the backend's exclude_unset PATCH from
      // touching job-costing when the seed never confirmed the real value.
      if (projectSeedState === "loaded" || projectSeedState === "none-needed") {
        const currentProjectId = editProject?.id ?? null;
        const storedProjectId = editAsset?.project_id ?? null;
        if (currentProjectId !== storedProjectId) {
          body.project_id = currentProjectId;
        }
      }
      if (!editGuardedDisabled) {
        Object.assign(body, {
          cost: parseFloat(editForm.cost),
          salvage_value: parseFloat(editForm.salvage_value),
          useful_life_months: parseInt(editForm.useful_life_months),
          depreciation_method: editForm.depreciation_method,
          total_estimated_units:
            editForm.depreciation_method === "units_of_production"
              ? parseFloat(editForm.total_estimated_units)
              : null,
          acquired_date: editForm.acquired_date,
          in_service_date: editForm.in_service_date || null,
          opening_accum_depr: parseFloat(editForm.opening_accum_depr) || 0,
          periods_elapsed_at_start: parseInt(editForm.periods_elapsed_at_start) || 0,
          asset_account_id: editForm.asset_account_id,
          accum_depr_account_id: editForm.accum_depr_account_id,
          depr_expense_account_id: editForm.depr_expense_account_id,
        });
      }
      return api<FixedAsset>(`/api/assets/${editAsset!.id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["assets"] });
      setEditAsset(null);
    },
  });

  const disposeMut = useMutation({
    mutationFn: () =>
      api<AssetDisposalResult>(`/api/assets/${disposeAsset!.id}/dispose`, {
        method: "POST",
        body: JSON.stringify({
          disposal_date: disposalDate,
          period_id: disposalPeriodId,
          proceeds: parseFloat(proceeds) || 0,
          proceeds_account_id: proceedsAccountId,
        }),
      }),
    onSuccess: (data) => {
      setDisposalResult(data);
      void qc.invalidateQueries({ queryKey: ["assets"] });
    },
  });

  // ── Schedule dialog (GAAP / Tax tabs + tax-profile form) ──────────────────
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleAsset, setScheduleAsset] = useState<FixedAsset | null>(null);
  const [scheduleTab, setScheduleTab] = useState<"gaap" | "tax">("gaap");
  const emptyTaxProfile = { macrs_class: "5", sec179_amount: "0", bonus_pct: "0", tax_basis_override: "" };
  const [taxProfileForm, setTaxProfileForm] = useState(emptyTaxProfile);

  const scheduleQ = useQuery(assetScheduleQueryOptions(scheduleAsset?.id));
  // Initialise the profile form from what is SAVED, not zeros — a reflexive
  // "Save Tax Profile" on a dialog that opened at defaults used to silently
  // wipe the asset's §179/bonus elections.
  useEffect(() => {
    const p = scheduleQ.data?.profile;
    if (p) {
      setTaxProfileForm({
        macrs_class: String(p.macrs_class),
        sec179_amount: String(p.sec179_amount ?? "0"),
        bonus_pct: String(p.bonus_pct ?? "0"),
        tax_basis_override: p.tax_basis_override == null ? "" : String(p.tax_basis_override),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleQ.data?.profile]);

  const taxProfileMut = useMutation({
    mutationFn: () =>
      api<AssetScheduleResponse>(`/api/assets/${scheduleAsset!.id}/tax-profile`, {
        method: "PUT",
        body: JSON.stringify({
          macrs_class: taxProfileForm.macrs_class,
          sec179_amount: parseFloat(taxProfileForm.sec179_amount) || 0,
          bonus_pct: parseFloat(taxProfileForm.bonus_pct) || 0,
          tax_basis_override: taxProfileForm.tax_basis_override === "" ? null : parseFloat(taxProfileForm.tax_basis_override),
        } satisfies TaxProfilePayload),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["asset-schedule", scheduleAsset?.id] });
      void qc.invalidateQueries({ queryKey: ["assets"] });
    },
  });

  // ── Revise estimate dialog ─────────────────────────────────────────────────
  const [reviseOpen, setReviseOpen] = useState(false);
  const [reviseAsset, setReviseAsset] = useState<FixedAsset | null>(null);
  const [newLife, setNewLife] = useState("");
  const [newSalvage, setNewSalvage] = useState("");
  const [effectivePeriodId, setEffectivePeriodId] = useState("");
  const [reviseReason, setReviseReason] = useState("");
  const [reviseResult, setReviseResult] = useState<boolean>(false);
  const [convertToStraightLine, setConvertToStraightLine] = useState(false);

  const reviseScheduleQ = useQuery(assetScheduleQueryOptions(reviseOpen ? reviseAsset?.id : undefined));

  // Elapsed periods = periods already depreciated before the register started
  // tracking this asset (migrated assets) + periods posted since. Both terms
  // are required — periods_elapsed_at_start alone misses everything posted
  // since go-live, and posted-count alone misses migrated history (see M6).
  const reviseElapsedPeriods =
    (reviseAsset?.periods_elapsed_at_start ?? 0) +
    (reviseScheduleQ.data?.gaap ?? []).filter((r) => r.posted).length;
  const reviseCurrentLife = reviseAsset?.useful_life_months ?? 0;
  const reviseCurrentSalvage = reviseAsset ? parseFloat(reviseAsset.salvage_value) : 0;
  // Only fields the user actually changed are sent — omission, not a
  // hardcoded method, is what keeps the backend from silently normalizing a
  // declining-balance or non-depreciating asset into straight-line (H2).
  const reviseLifeChanged = newLife !== "" && parseInt(newLife) !== reviseCurrentLife;
  const reviseSalvageChanged = newSalvage !== "" && parseFloat(newSalvage) !== reviseCurrentSalvage;
  const reviseMethodDiffers = !!reviseAsset && reviseAsset.depreciation_method !== "straight_line";
  const reviseMethodChanged = reviseMethodDiffers && convertToStraightLine;
  const reviseHasChange = reviseLifeChanged || reviseSalvageChanged || reviseMethodChanged;
  const reviseEffLife = reviseLifeChanged ? parseInt(newLife) : reviseCurrentLife;
  const reviseEffSalvage = reviseSalvageChanged ? parseFloat(newSalvage) : reviseCurrentSalvage;
  const reviseRemainingMonths = reviseEffLife - reviseElapsedPeriods;
  const reviseNbv = reviseAsset ? parseFloat(reviseAsset.net_book_value) : 0;
  const revisePreviewError =
    reviseRemainingMonths <= 0
      ? "new life must exceed periods already elapsed"
      : reviseEffSalvage > reviseNbv
        ? "new salvage value cannot exceed net book value"
        : null;
  const revisePreviewPerPeriod = revisePreviewError ? null : (reviseNbv - reviseEffSalvage) / reviseRemainingMonths;

  const reviseMut = useMutation({
    mutationFn: () =>
      api(`/api/assets/${reviseAsset!.id}/revise-estimate`, {
        method: "POST",
        body: JSON.stringify({
          ...(reviseLifeChanged ? { new_useful_life_months: parseInt(newLife) } : {}),
          ...(reviseSalvageChanged ? { new_salvage_value: parseFloat(newSalvage) } : {}),
          ...(reviseMethodChanged ? { new_depreciation_method: "straight_line" } : {}),
          effective_period_id: effectivePeriodId,
          reason: reviseReason,
        }),
      }),
    onSuccess: () => {
      setReviseResult(true);
      void qc.invalidateQueries({ queryKey: ["assets"] });
      void qc.invalidateQueries({ queryKey: ["asset-schedule", reviseAsset?.id] });
    },
  });

  // ── GL tie-out banner ──────────────────────────────────────────────────────
  const tieoutQ = useQuery(assetGlTieoutQueryOptions());
  const unbalancedAccounts = (tieoutQ.data?.rows ?? []).filter((a) => a.is_balanced === false);

  const assets = assetsQ.data ?? [];
  const openPeriods = (periodsQ.data ?? []).filter((p) => p.status === "open");

  function openDisposeDialog(a: FixedAsset | null) {
    const target = a ?? (disposableAssets.length === 1 ? disposableAssets[0] : null);
    setDisposeAsset(target);
    setDisposeOpen(true);
    setDisposalDate(todayISO());
    setDisposalPeriodId("");
    setProceeds("0");
    setProceedsAccountId("");
    setDisposalResult(null);
  }

  function openScheduleDialog(a: FixedAsset | null) {
    // One asset in the register ⇒ it's the only possible answer; picking it
    // for the user keeps the menu deep-link one click instead of three.
    const target = a ?? (assets.length === 1 ? assets[0] : null);
    setScheduleAsset(target);
    setScheduleOpen(true);
    setScheduleTab("gaap");
    setTaxProfileForm(emptyTaxProfile);
  }

  function openReviseDialog(a: FixedAsset) {
    setReviseAsset(a);
    setReviseOpen(true);
    setNewLife("");
    setNewSalvage("");
    setEffectivePeriodId("");
    setReviseReason("");
    setReviseResult(false);
    setConvertToStraightLine(false);
  }

  // Nav deep-links: the Fixed Assets menu has one row per action, all landing
  // on this page (see navLayouts.ts). Two triggers on purpose:
  //  1. the location hash — covers a fresh page load on /assets#dispose and
  //     arriving from another page;
  //  2. the NAV_HASH_CLICK_EVENT — covers re-clicking a row whose URL the user
  //     is ALREADY on. That navigation is a no-op (the router dedupes it — a
  //     location.state.key dependency was tried and verified not to re-fire),
  //     so the nav announces the click explicitly and we reopen regardless.
  const { hash } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (hash === "add") setAddOpen(true);
    else if (hash === "depreciation") { setDeprOpen(true); setDeprResult(null); }
    else if (hash === "dispose") openDisposeDialog(null);
    else if (hash === "schedule") openScheduleDialog(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash]);
  // The listener registers once, but the open* handlers close over `assets`
  // (for single-asset auto-select) — calling the mount-time closure would see
  // the pre-fetch empty list forever. The ref always points at this render's
  // handlers, so the once-registered listener never goes stale.
  const navClickHandlers = useRef({ openDisposeDialog, openScheduleDialog });
  navClickHandlers.current = { openDisposeDialog, openScheduleDialog };
  useEffect(() => {
    const onNavClick = (e: Event) => {
      const d = (e as CustomEvent<{ to: string; hash: string }>).detail;
      if (d?.to !== "/assets") return;
      if (d.hash === "add") setAddOpen(true);
      else if (d.hash === "depreciation") { setDeprOpen(true); setDeprResult(null); }
      else if (d.hash === "dispose") navClickHandlers.current.openDisposeDialog(null);
      else if (d.hash === "schedule") navClickHandlers.current.openScheduleDialog(null);
    };
    window.addEventListener(NAV_HASH_CLICK_EVENT, onNavClick);
    return () => window.removeEventListener(NAV_HASH_CLICK_EVENT, onNavClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Disposal reached from the menu has no row context, so the dialog asks which
  // asset. Already-disposed assets are excluded — the service rejects them.
  const disposableAssets = assets.filter((a) => a.status !== "disposed");

  const previewNbv = disposeAsset ? parseFloat(disposeAsset.net_book_value) : 0;
  const previewGainLoss = (parseFloat(proceeds) || 0) - previewNbv;

  function exportGaapCsv() {
    const rows = scheduleQ.data?.gaap ?? [];
    const blob = toCsv(
      ["period_no", "start_date", "end_date", "planned_amount", "posted"],
      rows.map((r) => [r.period_no, r.start_date, r.end_date, r.planned_amount, r.posted ? "true" : "false"]),
    );
    downloadCsvBlob(blob, `${scheduleAsset?.asset_no ?? "asset"}-gaap-schedule.csv`);
  }

  function exportTaxCsv() {
    const rows = scheduleQ.data?.tax ?? [];
    const blob = toCsv(
      ["tax_year", "convention", "sec179", "bonus", "macrs", "total"],
      rows.map((r) => [r.tax_year, r.convention, r.sec179, r.bonus, r.macrs, r.total]),
    );
    downloadCsvBlob(blob, `${scheduleAsset?.asset_no ?? "asset"}-tax-schedule.csv`);
  }

  // Client-side preview only — backend computes the authoritative amount from
  // NBV as of the effective period (see revise_estimate in
  // services/depreciation_schedule.py). reviseElapsedPeriods etc. are computed
  // above, alongside reviseMut, since both need the same "what changed" logic.

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AccountBalanceIcon color="primary" />
          <Typography variant="h5" fontWeight={600}>Fixed Assets</Typography>
          <HelpHint docPath="fixed-assets/README.md" label="Fixed assets help" />
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PlayArrowIcon />}
            onClick={() => { setDeprOpen(true); setDeprResult(null); }}
          >
            Run Depreciation
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
            Add Asset
          </Button>
        </Box>
      </Box>

      {/* GL tie-out banner — accumulated-depreciation register vs GL balance. */}
      {unbalancedAccounts.length > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate({ to: "/reports/asset-rollforward" })}>
              View Tie-Out
            </Button>
          }
        >
          {unbalancedAccounts.length} accumulated-depreciation account
          {unbalancedAccounts.length === 1 ? "" : "s"} don't tie to the GL:{" "}
          {unbalancedAccounts.map((a) => `${a.account_code} (${fmtMoney(a.delta)} off)`).join(", ")}
        </Alert>
      )}

      {/* Table */}
      {assetsQ.isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" } }}>
                <TableCell>Asset #</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Method</TableCell>
                <TableCell align="right">Cost</TableCell>
                <TableCell align="right">Accum. Depr.</TableCell>
                <TableCell align="right">Net Book Value</TableCell>
                <TableCell>Acquired</TableCell>
                <TableCell>In Service</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assets.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 6, color: "text.secondary" }}>
                    No assets yet — click Add Asset to create the first one.
                  </TableCell>
                </TableRow>
              )}
              {assets.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell sx={{ fontFamily: "monospace", fontWeight: 600 }}>{a.asset_no}</TableCell>
                  <TableCell>{a.description}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{a.asset_class ?? "—"}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      <Chip
                        label={a.status}
                        size="small"
                        color={STATUS_COLOR[a.status] ?? "default"}
                        variant="outlined"
                      />
                      {a.depreciation_gap > 0 && (
                        <Tooltip title="Open periods since in-service with no depreciation posted">
                          <Chip
                            label={`${a.depreciation_gap} period${a.depreciation_gap === 1 ? "" : "s"} behind`}
                            size="small"
                            color="warning"
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>
                    {a.depreciation_method.replace(/_/g, " ")}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(a.cost)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                    {fmtMoney(a.accumulated_depreciation)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace" }}>
                    {fmtMoney(a.net_book_value)}
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{a.acquired_date}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{a.in_service_date ?? "—"}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEditDialog(a)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {(a.status === "acquired" || a.status === "idle") && (
                      <Tooltip title="Place in Service">
                        <IconButton size="small"
                          onClick={() => { setPlaceInServiceAsset(a); setPlaceInServiceDate(todayISO()); }}
                        >
                          <PlayCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {a.status !== "disposed" && (
                      <Tooltip title="Dispose / Sell">
                        <IconButton size="small" onClick={() => openDisposeDialog(a)}>
                          <SellOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Schedule (GAAP / Tax)">
                      <IconButton size="small" onClick={() => openScheduleDialog(a)}>
                        <ScheduleIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {a.status !== "disposed" && (
                      <Tooltip title="Revise Estimate">
                        <IconButton size="small" onClick={() => openReviseDialog(a)}>
                          <EditCalendarIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Run depreciation dialog */}
      <Dialog open={deprOpen} onClose={() => setDeprOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Run Depreciation</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {deprResult ? (
            <Alert severity="success">
              Posted depreciation for <strong>{deprResult.count}</strong> asset(s) — total{" "}
              <strong>{fmtMoney(deprResult.total)}</strong>.
            </Alert>
          ) : (
            <FormControl fullWidth size="small">
              <InputLabel>Fiscal Period</InputLabel>
              <Select
                value={selectedPeriodId}
                label="Fiscal Period"
                onChange={(e) => setSelectedPeriodId(e.target.value)}
              >
                {openPeriods.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    Period {p.period_no} ({p.start_date} – {p.end_date})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {deprMut.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {String(deprMut.error)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeprOpen(false)}>Close</Button>
          {!deprResult && (
            <Button
              variant="contained"
              disabled={!selectedPeriodId || deprMut.isPending}
              onClick={() => deprMut.mutate(selectedPeriodId)}
            >
              {deprMut.isPending ? "Running…" : "Run"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Add asset dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Fixed Asset</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          {(["asset_no", "description", "asset_class"] as const).map((f) => (
            <TextField
              key={f}
              label={f === "asset_no" ? "Asset #" : f === "asset_class" ? "Asset Class" : "Description"}
              size="small" fullWidth
              value={newAsset[f]}
              onChange={(e) => setNewAsset((p) => ({ ...p, [f]: e.target.value }))}
            />
          ))}
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField label="Acquired Date" type="date" size="small" fullWidth
              InputLabelProps={{ shrink: true }}
              value={newAsset.acquired_date}
              onChange={(e) => setNewAsset((p) => ({ ...p, acquired_date: e.target.value }))}
            />
            <TextField label="In Service Date" type="date" size="small" fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Leave blank to place in service later"
              value={newAsset.in_service_date}
              onChange={(e) => setNewAsset((p) => ({ ...p, in_service_date: e.target.value }))}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField label="Cost" type="number" size="small" fullWidth
              value={newAsset.cost}
              onChange={(e) => setNewAsset((p) => ({ ...p, cost: e.target.value }))}
            />
            <TextField label="Salvage Value" type="number" size="small" fullWidth
              value={newAsset.salvage_value}
              onChange={(e) => setNewAsset((p) => ({ ...p, salvage_value: e.target.value }))}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField label="Useful Life (months)" type="number" size="small" fullWidth
              value={newAsset.useful_life_months}
              onChange={(e) => setNewAsset((p) => ({ ...p, useful_life_months: e.target.value }))}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Method</InputLabel>
              <Select value={newAsset.depreciation_method} label="Method"
                onChange={(e) => setNewAsset((p) => ({ ...p, depreciation_method: e.target.value }))}
              >
                <MenuItem value="straight_line">Straight Line</MenuItem>
                <MenuItem value="declining_balance_150">Declining Balance 150%</MenuItem>
                <MenuItem value="declining_balance_200">Declining Balance 200%</MenuItem>
                <MenuItem value="units_of_production">Units of Production</MenuItem>
                <MenuItem value="none">None</MenuItem>
              </Select>
            </FormControl>
          </Box>
          {addShowUnits && (
            <TextField label="Total Estimated Units (lifetime)" type="number" size="small" fullWidth
              error={addUnitsInvalid}
              helperText={
                addUnitsInvalid
                  ? "Required for units of production — enter a number greater than 0."
                  : "Total units the asset is expected to produce over its life (e.g. machine hours). Depreciation is spread across this many units. Useful life is still required for reporting."
              }
              value={newAsset.total_estimated_units}
              onChange={(e) => setNewAsset((p) => ({ ...p, total_estimated_units: e.target.value }))}
            />
          )}

          <AccountPicker label="Asset Account" value={newAsset.asset_account_id}
            onChange={(id) => setNewAsset((p) => ({ ...p, asset_account_id: id }))} />
          <AccountPicker label="Accumulated Depreciation Account" value={newAsset.accum_depr_account_id}
            onChange={(id) => setNewAsset((p) => ({ ...p, accum_depr_account_id: id }))} />
          <AccountPicker label="Depreciation Expense Account" value={newAsset.depr_expense_account_id}
            onChange={(id) => setNewAsset((p) => ({ ...p, depr_expense_account_id: id }))} />

          <FormControlLabel
            control={
              <Checkbox
                checked={isMigrated}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setIsMigrated(checked);
                  if (checked) {
                    setNewAsset((p) => ({
                      ...p,
                      purchase_method: "carried_over",
                      funding_account_id: "",
                    }));
                  } else {
                    setNewAsset((p) => ({
                      ...p,
                      purchase_method: p.purchase_method === "carried_over" ? "cash" : p.purchase_method,
                      equity_account_id: "",
                      opening_accum_depr: "0",
                      periods_elapsed_at_start: "0",
                    }));
                  }
                }}
              />
            }
            label="Carried over from another system"
          />
          {isMigrated ? (
            <>
              <Alert severity="info">
                Posts cost and opening depreciation to the GL, crediting Opening Balance Equity
                for net book value (${addNbv.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}).
                Do not also include this asset&apos;s dollars in a trial-balance import — that would double-count.
              </Alert>
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField label="Months Already Depreciated" type="number" size="small" fullWidth
                  helperText="e.g. 36 if 3 years of monthly depreciation already happened"
                  value={newAsset.periods_elapsed_at_start}
                  onChange={(e) => setNewAsset((p) => ({ ...p, periods_elapsed_at_start: e.target.value }))}
                />
                <TextField label="Opening Accumulated Depreciation" type="number" size="small" fullWidth
                  helperText="Dollar total from the prior system"
                  value={newAsset.opening_accum_depr}
                  onChange={(e) => setNewAsset((p) => ({ ...p, opening_accum_depr: e.target.value }))}
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Net book value = cost − opening accum. ={" "}
                <strong>
                  ${addNbv.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </strong>
                {" "}(credited to equity)
              </Typography>
              <AccountPicker
                label="Equity Account (optional)"
                value={newAsset.equity_account_id}
                accountTypes={["equity"]}
                helperText="Defaults to Opening Balance Equity when left blank"
                onChange={(id) => setNewAsset((p) => ({ ...p, equity_account_id: id }))}
              />
              <OpeningPairCrossCheck
                cost={newAsset.cost}
                salvage={newAsset.salvage_value}
                lifeMonths={newAsset.useful_life_months}
                opening={newAsset.opening_accum_depr}
                months={newAsset.periods_elapsed_at_start}
                onMonthsChange={(v) => setNewAsset((p) => ({ ...p, periods_elapsed_at_start: v }))}
              />
            </>
          ) : (
            <>
              <FormControl size="small" fullWidth required error={addPurchaseMethodInvalid}>
                <InputLabel>Purchase Method</InputLabel>
                <Select
                  value={newAsset.purchase_method}
                  label="Purchase Method"
                  onChange={(e) => setNewAsset((p) => ({
                    ...p,
                    purchase_method: e.target.value,
                    funding_account_id:
                      e.target.value === "ap" || e.target.value === "carried_over"
                        ? ""
                        : p.funding_account_id,
                  }))}
                >
                  <MenuItem value="cash">Cash / Bank</MenuItem>
                  <MenuItem value="loan">Loan / Notes Payable</MenuItem>
                  <MenuItem value="lease">Lease (capitalize full cost)</MenuItem>
                  <MenuItem value="ap">Accounts Payable</MenuItem>
                </Select>
              </FormControl>
              {newAsset.purchase_method === "ap" ? (
                <Alert severity="info">
                  Credits the system Accounts Payable control account automatically.
                </Alert>
              ) : (
                <AccountPicker
                  label={
                    newAsset.purchase_method === "cash"
                      ? "Bank / Cash Account"
                      : newAsset.purchase_method === "loan"
                        ? "Notes Payable Account"
                        : "Lease Liability Account"
                  }
                  value={newAsset.funding_account_id}
                  accountTypes={
                    newAsset.purchase_method === "cash" ? ["asset"] : ["liability"]
                  }
                  helperText={addFundingInvalid ? "Required for this purchase method" : undefined}
                  onChange={(id) => setNewAsset((p) => ({ ...p, funding_account_id: id }))}
                />
              )}
            </>
          )}
          {addMut.error && <Alert severity="error">{String(addMut.error)}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={addMut.isPending || addCreateDisabled}
            onClick={() => addMut.mutate({ ...newAsset, isMigrated })}
          >
            {addMut.isPending ? "Posting…" : "Create & Post"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit asset dialog — always-editable metadata + guarded setup fields
          (MONEY-FIXED-ASSETS-003 §B). The setup section is disabled up front
          once posted depreciation is visible on the row; the backend is the
          final word and 409s with the specific reason if this ever disagrees. */}
      <Dialog open={!!editAsset} onClose={() => setEditAsset(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Asset</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <TextField label="Asset #" size="small" fullWidth
            value={editForm.asset_no}
            onChange={(e) => setEditForm((p) => ({ ...p, asset_no: e.target.value }))}
          />
          <TextField label="Description" size="small" fullWidth
            value={editForm.description}
            onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
          />
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField label="Asset Class" size="small" fullWidth
              value={editForm.asset_class}
              onChange={(e) => setEditForm((p) => ({ ...p, asset_class: e.target.value }))}
            />
            <TextField label="Serial #" size="small" fullWidth
              value={editForm.serial_no}
              onChange={(e) => setEditForm((p) => ({ ...p, serial_no: e.target.value }))}
            />
          </Box>
          <TextField label="Location" size="small" fullWidth
            value={editForm.location}
            onChange={(e) => setEditForm((p) => ({ ...p, location: e.target.value }))}
          />
          <ProjectSelect
            value={editProject}
            onChange={setEditProject}
            statusFilter={["bidding", "awarded", "active", "hold", "closed", "cancelled"]}
            showStatusDropdown={false}
            placeholder="No project"
          />
          {projectSeedState === "failed" && (
            <Alert severity="warning" variant="outlined" sx={{ py: 0 }}>
              Couldn't load the linked project — project unchanged.
            </Alert>
          )}
          <TextField label="Notes" size="small" fullWidth multiline minRows={2}
            value={editForm.notes}
            onChange={(e) => setEditForm((p) => ({ ...p, notes: e.target.value }))}
          />

          <Divider />
          <Typography variant="subtitle2">Setup (locked after first depreciation)</Typography>
          {editGuardedDisabled && (
            <Alert severity="info" variant="outlined">
              {editIsDisposed
                ? "Disposed assets keep their history; only descriptive fields are editable."
                : "This asset has posted depreciation, so cost, salvage, life, method, dates, and accounts are locked. Change life, salvage, or method via Revise Estimate instead."}
            </Alert>
          )}
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField label="Cost" type="number" size="small" fullWidth
              disabled={editGuardedDisabled}
              value={editForm.cost}
              onChange={(e) => setEditForm((p) => ({ ...p, cost: e.target.value }))}
            />
            <TextField label="Salvage Value" type="number" size="small" fullWidth
              disabled={editGuardedDisabled}
              value={editForm.salvage_value}
              onChange={(e) => setEditForm((p) => ({ ...p, salvage_value: e.target.value }))}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField label="Useful Life (months)" type="number" size="small" fullWidth
              disabled={editGuardedDisabled}
              value={editForm.useful_life_months}
              onChange={(e) => setEditForm((p) => ({ ...p, useful_life_months: e.target.value }))}
            />
            <FormControl size="small" fullWidth disabled={editGuardedDisabled}>
              <InputLabel>Method</InputLabel>
              <Select value={editForm.depreciation_method} label="Method"
                onChange={(e) => setEditForm((p) => ({ ...p, depreciation_method: e.target.value }))}
              >
                <MenuItem value="straight_line">Straight Line</MenuItem>
                <MenuItem value="declining_balance_150">Declining Balance 150%</MenuItem>
                <MenuItem value="declining_balance_200">Declining Balance 200%</MenuItem>
                <MenuItem value="units_of_production">Units of Production</MenuItem>
                <MenuItem value="none">None</MenuItem>
              </Select>
            </FormControl>
          </Box>
          {editShowUnits && (
            <TextField label="Total Estimated Units (lifetime)" type="number" size="small" fullWidth
              disabled={editGuardedDisabled}
              error={editUnitsInvalid}
              helperText={
                editUnitsInvalid
                  ? "Required for units of production — enter a number greater than 0."
                  : "Total units the asset is expected to produce over its life (e.g. machine hours). Depreciation is spread across this many units. Useful life is still required for reporting."
              }
              value={editForm.total_estimated_units}
              onChange={(e) => setEditForm((p) => ({ ...p, total_estimated_units: e.target.value }))}
            />
          )}
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField label="Acquired Date" type="date" size="small" fullWidth
              disabled={editGuardedDisabled}
              InputLabelProps={{ shrink: true }}
              value={editForm.acquired_date}
              onChange={(e) => setEditForm((p) => ({ ...p, acquired_date: e.target.value }))}
            />
            <TextField label="In Service Date" type="date" size="small" fullWidth
              disabled={editGuardedDisabled}
              InputLabelProps={{ shrink: true }}
              value={editForm.in_service_date}
              onChange={(e) => setEditForm((p) => ({ ...p, in_service_date: e.target.value }))}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField label="Months Already Depreciated" type="number" size="small" fullWidth
              disabled={editGuardedDisabled}
              value={editForm.periods_elapsed_at_start}
              onChange={(e) => setEditForm((p) => ({ ...p, periods_elapsed_at_start: e.target.value }))}
            />
            <TextField label="Opening Accumulated Depreciation" type="number" size="small" fullWidth
              disabled={editGuardedDisabled}
              value={editForm.opening_accum_depr}
              onChange={(e) => setEditForm((p) => ({ ...p, opening_accum_depr: e.target.value }))}
            />
          </Box>
          {!editGuardedDisabled && (
            <OpeningPairCrossCheck
              cost={editForm.cost}
              salvage={editForm.salvage_value}
              lifeMonths={editForm.useful_life_months}
              opening={editForm.opening_accum_depr}
              months={editForm.periods_elapsed_at_start}
              onMonthsChange={(v) => setEditForm((p) => ({ ...p, periods_elapsed_at_start: v }))}
              autoApply={false}
            />
          )}

          <AccountPicker label="Asset Account" value={editForm.asset_account_id} disabled={editGuardedDisabled}
            onChange={(id) => setEditForm((p) => ({ ...p, asset_account_id: id }))} />
          <AccountPicker label="Accumulated Depreciation Account" value={editForm.accum_depr_account_id} disabled={editGuardedDisabled}
            onChange={(id) => setEditForm((p) => ({ ...p, accum_depr_account_id: id }))} />
          <AccountPicker label="Depreciation Expense Account" value={editForm.depr_expense_account_id} disabled={editGuardedDisabled}
            onChange={(id) => setEditForm((p) => ({ ...p, depr_expense_account_id: id }))} />

          {editMut.error && <Alert severity="error">{errorMessage(editMut.error)}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditAsset(null)}>Cancel</Button>
          <Button variant="contained" disabled={editMut.isPending || editUnitsInvalid}
            onClick={() => editMut.mutate()}
          >
            {editMut.isPending ? "Saving…" : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Place in service dialog */}
      <Dialog open={!!placeInServiceAsset} onClose={() => setPlaceInServiceAsset(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Place in Service</DialogTitle>
        <DialogContent sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {placeInServiceAsset?.asset_no} — {placeInServiceAsset?.description}
          </Typography>
          <TextField label="In Service Date" type="date" size="small" fullWidth
            InputLabelProps={{ shrink: true }}
            value={placeInServiceDate}
            onChange={(e) => setPlaceInServiceDate(e.target.value)}
          />
          <Typography variant="caption" color="text.secondary">
            Depreciation only runs against assets that are in service.
          </Typography>
          {placeInServiceMut.error && <Alert severity="error">{String(placeInServiceMut.error)}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlaceInServiceAsset(null)}>Cancel</Button>
          <Button variant="contained" disabled={placeInServiceMut.isPending}
            onClick={() => placeInServiceMut.mutate({ id: placeInServiceAsset!.id, inServiceDate: placeInServiceDate })}
          >
            {placeInServiceMut.isPending ? "Saving…" : "Place in Service"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dispose / sell dialog */}
      <Dialog open={disposeOpen} onClose={() => setDisposeOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Dispose / Sell Asset</DialogTitle>
        <DialogContent sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          {disposeAsset ? (
            <Typography variant="body2" color="text.secondary">
              {disposeAsset.asset_no} — {disposeAsset.description}
            </Typography>
          ) : (
            !disposalResult && (disposableAssets.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Nothing to dispose — there are no assets that aren't already disposed.
                </Typography>
              </Box>
            ) : (
              <Autocomplete
                size="small"
                options={disposableAssets}
                getOptionLabel={(a: FixedAsset) => `${a.asset_no} · ${a.description}`}
                value={disposeAsset}
                onChange={(_e, v) => setDisposeAsset(v)}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => <TextField {...params} label="Asset" />}
              />
            ))
          )}
          {disposalResult ? (
            <Alert severity={disposalResult.gain_loss >= 0 ? "success" : "warning"}>
              Disposed. {disposalResult.gain_loss >= 0 ? "Gain" : "Loss"} of{" "}
              <strong>{fmtMoney(Math.abs(disposalResult.gain_loss))}</strong> posted.
            </Alert>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary">
                Net book value: <strong>{disposeAsset ? fmtMoney(disposeAsset.net_book_value) : "—"}</strong>
              </Typography>
              <TextField label="Disposal Date" type="date" size="small" fullWidth
                InputLabelProps={{ shrink: true }}
                value={disposalDate}
                onChange={(e) => setDisposalDate(e.target.value)}
              />
              <FormControl fullWidth size="small">
                <InputLabel>Fiscal Period</InputLabel>
                <Select
                  value={disposalPeriodId}
                  label="Fiscal Period"
                  onChange={(e) => setDisposalPeriodId(e.target.value)}
                >
                  {openPeriods.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      Period {p.period_no} ({p.start_date} – {p.end_date})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField label="Proceeds" type="number" size="small" fullWidth
                helperText="0 if scrapped with no sale proceeds"
                value={proceeds}
                onChange={(e) => setProceeds(e.target.value)}
              />
              <AccountPicker label="Deposit Proceeds To" value={proceedsAccountId}
                onChange={setProceedsAccountId} />
              <Alert severity={previewGainLoss >= 0 ? "success" : "warning"} variant="outlined">
                Estimated {previewGainLoss >= 0 ? "gain" : "loss"}: {fmtMoney(Math.abs(previewGainLoss))}
              </Alert>
              {disposeMut.error && <Alert severity="error">{String(disposeMut.error)}</Alert>}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisposeOpen(false)}>{disposalResult ? "Close" : "Cancel"}</Button>
          {!disposalResult && (
            <Button
              variant="contained"
              color="warning"
              disabled={
                !disposeAsset || !disposalPeriodId || !proceedsAccountId || disposeMut.isPending
              }
              onClick={() => disposeMut.mutate()}
            >
              {disposeMut.isPending ? "Disposing…" : "Dispose"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Schedule dialog — GAAP by period / Tax by year, + tax-profile form. */}
      <Dialog open={scheduleOpen} onClose={() => setScheduleOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Depreciation Schedule</DialogTitle>
        <DialogContent sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          {scheduleAsset ? (
            <Typography variant="body2" color="text.secondary">
              {scheduleAsset.asset_no} — {scheduleAsset.description}
            </Typography>
          ) : assets.length === 0 ? (
            /* Reached from the menu with an empty register, the dialog used to
               show a bare picker with nothing in it — it looked broken. Say
               what's missing and offer the next step instead. */
            <Box sx={{ textAlign: "center", py: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No assets yet — a depreciation schedule belongs to an asset.
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />}
                onClick={() => { setScheduleOpen(false); setAddOpen(true); }}>
                Record an Asset
              </Button>
            </Box>
          ) : (
            <Autocomplete
              size="small"
              options={assets}
              getOptionLabel={(a: FixedAsset) => `${a.asset_no} · ${a.description}`}
              value={scheduleAsset}
              onChange={(_e, v) => setScheduleAsset(v)}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => <TextField {...params} label="Asset" />}
            />
          )}

          {scheduleAsset && (
            <>
              <Tabs value={scheduleTab} onChange={(_e, v) => setScheduleTab(v)} sx={{ minHeight: 36 }}>
                <Tab value="gaap" label="GAAP by Period" sx={{ minHeight: 36 }} />
                <Tab value="tax" label="Tax by Year" sx={{ minHeight: 36 }} />
              </Tabs>

              {scheduleQ.isLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {!scheduleQ.isLoading && scheduleTab === "gaap" && (
                <Box>
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                    <Button
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={exportGaapCsv}
                      disabled={!scheduleQ.data?.gaap?.length}
                    >
                      Export CSV
                    </Button>
                  </Box>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Year</TableCell>
                        <TableCell>Period</TableCell>
                        <TableCell>Start</TableCell>
                        <TableCell>End</TableCell>
                        <TableCell align="right">Planned Amount</TableCell>
                        <TableCell align="center">Posted</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(scheduleQ.data?.gaap ?? []).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 3, color: "text.secondary" }}>
                            No GAAP schedule yet — place the asset in service to generate one.
                          </TableCell>
                        </TableRow>
                      )}
                      {/* Keyed by start_date + period_no: period_no alone repeats
                          across fiscal years on a multi-year schedule (L3). */}
                      {(scheduleQ.data?.gaap ?? []).map((r) => (
                        <TableRow key={`${r.start_date}-${r.period_no}`}>
                          <TableCell sx={{ color: "text.secondary" }}>{r.start_date.slice(0, 4)}</TableCell>
                          <TableCell>{r.period_no}</TableCell>
                          <TableCell sx={{ color: "text.secondary" }}>{r.start_date}</TableCell>
                          <TableCell sx={{ color: "text.secondary" }}>{r.end_date}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: "monospace" }}>
                            {fmtMoney(r.planned_amount)}
                          </TableCell>
                          <TableCell align="center">{r.posted ? "✓" : ""}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}

              {!scheduleQ.isLoading && scheduleTab === "tax" && (
                <Box>
                  {scheduleQ.data?.tax === null || scheduleQ.data?.tax === undefined ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      No tax profile set for this asset yet — set one below to generate the tax schedule.
                    </Alert>
                  ) : (
                    <>
                      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                        <Button
                          size="small"
                          startIcon={<DownloadIcon />}
                          onClick={exportTaxCsv}
                          disabled={!scheduleQ.data?.tax?.length}
                        >
                          Export CSV
                        </Button>
                      </Box>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Tax Year</TableCell>
                            <TableCell>Convention</TableCell>
                            <TableCell align="right">§179</TableCell>
                            <TableCell align="right">Bonus</TableCell>
                            <TableCell align="right">MACRS</TableCell>
                            <TableCell align="right">Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(scheduleQ.data?.tax ?? []).map((r) => (
                            <TableRow key={r.tax_year}>
                              <TableCell>{r.tax_year}</TableCell>
                              <TableCell sx={{ color: "text.secondary" }}>{r.convention}</TableCell>
                              <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(r.sec179)}</TableCell>
                              <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(r.bonus)}</TableCell>
                              <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(r.macrs)}</TableCell>
                              <TableCell align="right" sx={{ fontFamily: "monospace", fontWeight: 600 }}>{fmtMoney(r.total)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </>
                  )}

                  {/* Tax profile form — set/update, always available so an
                      existing profile can be corrected too. */}
                  <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                      {scheduleQ.data?.tax ? "Update Tax Profile" : "Set Tax Profile"}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>MACRS Class</InputLabel>
                        <Select
                          value={taxProfileForm.macrs_class}
                          label="MACRS Class"
                          onChange={(e) => setTaxProfileForm((p) => ({ ...p, macrs_class: e.target.value }))}
                        >
                          {MACRS_CLASSES.map((c) => (
                            <MenuItem key={c} value={c}>{c}-year</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        label="§179 Amount" type="number" size="small" sx={{ minWidth: 140 }}
                        value={taxProfileForm.sec179_amount}
                        onChange={(e) => setTaxProfileForm((p) => ({ ...p, sec179_amount: e.target.value }))}
                      />
                      <TextField
                        label="Bonus %" type="number" size="small" sx={{ minWidth: 100 }}
                        value={taxProfileForm.bonus_pct}
                        onChange={(e) => setTaxProfileForm((p) => ({ ...p, bonus_pct: e.target.value }))}
                      />
                      <TextField
                        label="Tax Basis Override" type="number" size="small" sx={{ minWidth: 160 }}
                        helperText="Leave blank to use cost"
                        value={taxProfileForm.tax_basis_override}
                        onChange={(e) => setTaxProfileForm((p) => ({ ...p, tax_basis_override: e.target.value }))}
                      />
                    </Box>
                    {taxProfileMut.error && (
                      <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(taxProfileMut.error)}</Alert>
                    )}
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                      <Button
                        variant="contained" size="small"
                        disabled={taxProfileMut.isPending}
                        onClick={() => taxProfileMut.mutate()}
                      >
                        {taxProfileMut.isPending ? "Saving…" : "Save Tax Profile"}
                      </Button>
                    </Box>
                  </Paper>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Revise estimate dialog — ASC 250 prospective change. */}
      <Dialog open={reviseOpen} onClose={() => setReviseOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Revise Estimate</DialogTitle>
        <DialogContent sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          {reviseAsset && (
            <Typography variant="body2" color="text.secondary">
              {reviseAsset.asset_no} — {reviseAsset.description}
            </Typography>
          )}
          {reviseResult ? (
            <Alert severity="success">Estimate revised. The schedule has been regenerated.</Alert>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary">
                Prospective only (ASC 250) — leave a field blank to keep it unchanged. Blocked once the
                effective period has a posting.
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField label="New Useful Life (months)" type="number" size="small" fullWidth
                  value={newLife}
                  onChange={(e) => setNewLife(e.target.value)}
                />
                <TextField label="New Salvage Value" type="number" size="small" fullWidth
                  value={newSalvage}
                  onChange={(e) => setNewSalvage(e.target.value)}
                />
              </Box>
              <Typography variant="body2">
                Method: <strong>{reviseAsset?.depreciation_method.replace(/_/g, " ")}</strong>
              </Typography>
              {reviseMethodDiffers && (
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={convertToStraightLine}
                        onChange={(e) => setConvertToStraightLine(e.target.checked)}
                      />
                    }
                    label="Also convert to straight-line (prospective)"
                  />
                  <Typography variant="caption" color="text.secondary" display="block">
                    Declining-balance history is kept — future periods become straight-line from net book value.
                  </Typography>
                </Box>
              )}
              <FormControl fullWidth size="small">
                <InputLabel>Effective Period</InputLabel>
                <Select
                  value={effectivePeriodId}
                  label="Effective Period"
                  onChange={(e) => setEffectivePeriodId(e.target.value)}
                >
                  {openPeriods.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      Period {p.period_no} ({p.start_date} – {p.end_date})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Reason" required multiline minRows={2} size="small" fullWidth
                value={reviseReason}
                onChange={(e) => setReviseReason(e.target.value)}
              />
              {!reviseHasChange ? (
                <Alert severity="info" variant="outlined">nothing changed</Alert>
              ) : revisePreviewPerPeriod !== null ? (
                <Alert severity="info" variant="outlined">
                  Preview: new per-period amount ≈ <strong>{fmtMoney(revisePreviewPerPeriod)}</strong>
                </Alert>
              ) : (
                <Alert severity="warning" variant="outlined">
                  Preview unavailable — {revisePreviewError}.
                </Alert>
              )}
              {reviseMut.error && <Alert severity="error">{errorMessage(reviseMut.error)}</Alert>}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviseOpen(false)}>{reviseResult ? "Close" : "Cancel"}</Button>
          {!reviseResult && (
            <Button
              variant="contained"
              disabled={!reviseAsset || !effectivePeriodId || !reviseReason.trim() || !reviseHasChange || reviseMut.isPending}
              onClick={() => reviseMut.mutate()}
            >
              {reviseMut.isPending ? "Submitting…" : "Submit Revision"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
